import type {
  CraftifyV1ArtistProgramApplication,
  CraftifyV1ArtistProgramMyReply,
  CraftifyV1ArtistProgramSocialAccount,
  CraftifyV1SubmitArtistProgramApplicationRequest,
  ViduV1Activity,
  ViduV1ArtistProgramSubmit,
} from '@/app/apis';
import {
  artistProgramGetActivity,
  artistProgramGetMy,
  artistProgramListActivities,
  artistProgramListMySubmits,
  artistProgramSignupActivity,
  artistProgramSubmitApplication,
  artistProgramSubmitWork,
} from '@/app/apis';
import type { SupportedLanguage } from '@/app/types';
import { normalizeArtistActivityText } from '../_localized';

type ApiError = { code?: number; message?: string; reason?: string };

export class ArtistProgramApiError extends Error {
  code?: number;
  reason?: string;

  constructor(error: ApiError, fallbackMessage: string) {
    super(error.message || error.reason || fallbackMessage);
    this.name = 'ArtistProgramApiError';
    this.code = error.code;
    this.reason = error.reason;
  }
}

export const isDuplicateArtistProgramSubmissionError = (error: unknown) =>
  error instanceof ArtistProgramApiError && error.code === 409 && error.reason === 'Conflict';

const assertNoApiError = (error: ApiError | undefined, fallbackMessage: string) => {
  if (error) throw new ArtistProgramApiError(error, fallbackMessage);
};

export type ArtistProgramActivity = ViduV1Activity;
export type ArtistProgramApplication = CraftifyV1ArtistProgramApplication;
export type ArtistProgramSocialAccount = CraftifyV1ArtistProgramSocialAccount;
export type ArtistProgramSubmit = ViduV1ArtistProgramSubmit & { target_id?: string };
export type ArtistProgramMyReply = CraftifyV1ArtistProgramMyReply;
export type ArtistProgramApplicationRequest = CraftifyV1SubmitArtistProgramApplicationRequest;
export type ArtistProgramApplyStatus = 'not_applied' | 'reviewing' | 'approved' | 'rejected';
export type ArtistProgramMy = ArtistProgramMyReply & { status: ArtistProgramApplyStatus };

const normalizeStatus = (status?: ArtistProgramApplication['status']): ArtistProgramApplyStatus =>
  status === 'reviewing' || status === 'approved' || status === 'rejected' ? status : 'not_applied';

export const normalizeMy = (data?: ArtistProgramMyReply): ArtistProgramMy => ({
  ...data,
  status: normalizeStatus(data?.application?.status),
});

export const getMy = async (): Promise<ArtistProgramMy> => {
  const response = await artistProgramGetMy();
  assertNoApiError(response.error, 'Failed to load application status.');
  return normalizeMy(response.data);
};

export const listActivityPage = async ({
  locale = 'en',
  pageSize,
  pageToken,
  signedUp,
}: {
  locale?: SupportedLanguage;
  pageSize?: number;
  pageToken?: string;
  signedUp?: boolean;
} = {}): Promise<{ activities: ArtistProgramActivity[]; nextPageToken: string }> => {
  const response = await artistProgramListActivities({
    query: {
      'pager.pagesz': pageSize,
      'pager.page_token': pageToken || undefined,
      signed_up: signedUp,
    },
  });
  assertNoApiError(response.error, 'Failed to load activities.');

  return {
    activities: (response.data?.activities ?? []).map((activity) => normalizeArtistActivityText(activity, locale)),
    nextPageToken: response.data?.next_page_token ?? '',
  };
};

export const getActivity = async (id: string, locale: SupportedLanguage = 'en') => {
  const response = await artistProgramGetActivity({ path: { id } });
  assertNoApiError(response.error, 'Failed to load activity details.');
  return response.data ? normalizeArtistActivityText(response.data, locale) : null;
};

export const signupActivity = async (activityId: string) => {
  if (!activityId) throw new Error('Activity id is required.');
  const response = await artistProgramSignupActivity({
    body: { activity_id: activityId },
    path: { activity_id: activityId },
  });
  assertNoApiError(response.error, 'Sign-up failed. Please try again later.');
};

export const submitWork = async ({
  activityId,
  workUrl,
  title,
  description,
}: {
  activityId: string;
  workUrl: string;
  title?: string;
  description?: string;
}) => {
  if (!activityId) throw new Error('Activity id is required.');
  const response = await artistProgramSubmitWork({
    body: { target_id: activityId, content: workUrl, title, description },
    path: { target_id: activityId },
  });
  assertNoApiError(response.error, 'Submission failed.');
  return response.data;
};

export const submitApplication = async (application: ArtistProgramApplicationRequest) => {
  const response = await artistProgramSubmitApplication({ body: application });
  assertNoApiError(response.error, 'Application failed. Please try again later.');
  return response.data;
};

export const listMySubmits = async ({ pageSize, pageToken }: { pageSize?: number; pageToken?: string } = {}) => {
  const response = await artistProgramListMySubmits({
    query: { 'pager.pagesz': pageSize, 'pager.page_token': pageToken || undefined },
  });
  assertNoApiError(response.error, 'Failed to load submission history.');
  return {
    submits: (response.data?.submits ?? []) as ArtistProgramSubmit[],
    nextPageToken: response.data?.next_page_token ?? '',
  };
};
